/**
 * Feature switches for the phased launch. The live values are one Firestore
 * document (settings/features); these are the fallback defaults used until
 * that document exists — chosen to exactly match the app's behaviour before
 * switches existed, so merely deploying this code changes nothing.
 *
 * Two presets capture the plan:
 *   REDACTED (launch week):  Travel (trimmed) + the Barga tab + a date-only
 *                            Home + the Me page + the People page and
 *                            Riccardo A.I. No photos/playlist/board, no
 *                            flight logging, no staying lists.
 *   FULL (week 4):           everything on; the Barga tab folds away into
 *                            a link on the Travel page instead.
 */

export const DEFAULT_FEATURES = {
  home: true, // full Dashboard; when off, "/" shows the date-only redacted home
  people: true,
  photos: true,
  playlist: true,
  board: true,
  me: true,
  bargaTab: false, // standalone Barga tab (phase-1 entrance)
  travelBargaLink: false, // Barga card on the Travel page (phase-2 entrance)
  travelFlights: true, // Log your flight + Who's on my flight
  travelStaying: true, // Where everyone's staying + Who's staying where
  concierge: true, // Riccardo A.I. chat FAB
};

/**
 * Admin-panel checkboxes, grouped by the page they change — so it's obvious
 * at a glance which version of Home/Travel/Barga guests are currently
 * seeing, rather than reading through a flat list of ten switches.
 */
export const FEATURE_GROUPS = [
  {
    title: "Home page",
    items: [["home", "Full dashboard (off = redacted, dates only)"]],
  },
  {
    title: "Travel page",
    items: [
      ["travelFlights", "Flight logging & who's on my flight"],
      ["travelStaying", "Where everyone's staying"],
      ["travelBargaLink", "Barga Guide linked from this page"],
    ],
  },
  {
    title: "Barga guide",
    items: [["bargaTab", "Own tab in the tab bar"]],
  },
  {
    title: "Other pages",
    items: [
      ["people", "People"],
      ["photos", "Photos"],
      ["playlist", "Playlist"],
      ["board", "Board"],
      ["me", "Me (profiles)"],
    ],
  },
  {
    title: "Riccardo A.I.",
    items: [["concierge", "Chat assistant (FAB on every page)"]],
  },
];

/** Flat [key, label] list, in display order — derived from FEATURE_GROUPS. */
export const FEATURE_LABELS = FEATURE_GROUPS.flatMap((g) => g.items);

export const PRESET_REDACTED = {
  home: false,
  // Added to the redacted launch on 2026-09-01. Everything a guest typed at
  // sign-up becomes visible to other guests the moment this goes true —
  // including contact details for anyone with `shareContact` on, which is
  // the default. Guests who registered BEFORE that date were told the
  // opposite in onboarding, so tell them before flipping it.
  people: true,
  photos: false,
  playlist: false,
  board: false,
  // Guests fill in their "about me" answers at sign-up, so they need
  // somewhere to go back and change them.
  me: true,
  bargaTab: true,
  travelBargaLink: false,
  // Flights stay OFF deliberately — a full-launch feature. The nudge toast
  // on Dashboard.jsx already checks this flag itself (see the comment
  // there), so it can't fire even if `home` is ever switched on early.
  travelFlights: false,
  // Added 2026-09-01, alongside People — the People page now links to this
  // section ("Want to see who's staying near you?"), so it has to be live
  // wherever People is, or that link goes nowhere.
  travelStaying: true,
  // Also added 2026-09-01. He reads the `directory` mirror, so he can only
  // tell a guest what the People page already shows them.
  concierge: true,
};

export const PRESET_FULL = {
  home: true,
  people: true,
  photos: true,
  playlist: true,
  board: true,
  me: true,
  bargaTab: false,
  travelBargaLink: true,
  travelFlights: true,
  travelStaying: true,
  concierge: true,
};
